import {
  type ContractorPayment,
  type ContractorPaymentAPI,
  type ContractorPaymentQuote,
  type ContractorPaymentQuoteAPI,
  type Fee,
  type FeeAPI,
  mapAddressFromAPI,
  PaymentMethod,
  PaymentMethodAPI,
} from '@omnipresentgroup/types';

/**
 * Mappers
 */

const transformPaymentTypeFromAPI = (
  paymentMethod: PaymentMethodAPI,
): PaymentMethod => {
  const mapping: Record<PaymentMethodAPI, PaymentMethod> = {
    [PaymentMethodAPI.MANUAL_BANK_TRANSFER]: PaymentMethod.MANUAL_BANK_TRANSFER,
    [PaymentMethodAPI.CREDIT_CARD]: PaymentMethod.CREDIT_CARD,
    [PaymentMethodAPI.DIRECT_DEBIT]: PaymentMethod.DIRECT_DEBIT,
  };
  return mapping[paymentMethod];
};

export const transformContractorPaymentQuoteFromAPI = (
  paymentQuoteAPI: ContractorPaymentQuoteAPI,
): ContractorPaymentQuote => ({
  quoteId: paymentQuoteAPI.quote_id,
  expiresAt: paymentQuoteAPI.expires_at,
  paymentType: transformPaymentTypeFromAPI(paymentQuoteAPI.payment_type),
  quoteSummary: {
    baseAmount: paymentQuoteAPI.quote_summary.base_amount,
    convertedAmount: paymentQuoteAPI.quote_summary.converted_amount,
    fees: paymentQuoteAPI.quote_summary.fees.map((feeAPI) =>
      transformContractorPaymentFeeFromApi(feeAPI),
    ),
    transferAmount: paymentQuoteAPI.quote_summary.transfer_amount,
    fx: paymentQuoteAPI.quote_summary.fx
      ? {
          conversion: {
            fromCurrency:
              paymentQuoteAPI.quote_summary.fx?.conversion.from_currency,
            toCurrency:
              paymentQuoteAPI.quote_summary.fx?.conversion.to_currency,
            effectiveRate:
              paymentQuoteAPI.quote_summary.fx?.conversion.effective_rate,
          },
          markupRate: paymentQuoteAPI.quote_summary.fx.markup_rate,
        }
      : undefined,
    vatRate: paymentQuoteAPI.quote_summary.vat_rate,
    country: paymentQuoteAPI.quote_summary.country,
  },
  invoiceBreakdown: paymentQuoteAPI.invoices_breakdown.map((quoteAPI) => ({
    baseAmount: quoteAPI.base_amount,
    convertedAmount: quoteAPI.converted_amount,
    fees: quoteAPI.fees.map((feeAPI) =>
      transformContractorPaymentFeeFromApi(feeAPI),
    ),
    transferAmount: quoteAPI.transfer_amount,
    fx: quoteAPI.fx
      ? {
          conversion: {
            fromCurrency: quoteAPI.fx?.conversion.from_currency,
            toCurrency: quoteAPI.fx?.conversion.to_currency,
            effectiveRate: quoteAPI.fx?.conversion.effective_rate,
          },
          markupRate: quoteAPI.fx.markup_rate,
        }
      : undefined,
    vatRate: quoteAPI.vat_rate,
    country: quoteAPI.country,
    invoiceId: quoteAPI.invoice_id,
    invoiceRef: quoteAPI.invoice_ref,
    contractor: quoteAPI.contractor,
  })),
});

export const transformContractorPaymentFromAPI = (
  paymentAPI: ContractorPaymentAPI,
): ContractorPayment => {
  const transferDetailsBankaccount =
    paymentAPI.transfer_details?.gb_bank_account ||
    paymentAPI.transfer_details?.iban_bank_account;

  return {
    id: paymentAPI.id,
    createdAt: paymentAPI.created_at,
    transferDetails:
      paymentAPI.transfer_details && transferDetailsBankaccount
        ? {
            bankAccount: {
              ownerName: transferDetailsBankaccount.owner_name,
              ownerAddress: mapAddressFromAPI(
                transferDetailsBankaccount.owner_address,
              ),
              ...(transferDetailsBankaccount.bank_account_type === 'GB'
                ? {
                    accountNumber: transferDetailsBankaccount.account_number,
                    sortCode: transferDetailsBankaccount.sort_code,
                    bankAccountType: 'GB',
                  }
                : {
                    iban: transferDetailsBankaccount.iban,
                    swiftBic: transferDetailsBankaccount.swift_bic,
                    bankAccountType: 'IBAN',
                  }),
            },
            wireReference: paymentAPI.transfer_details.wire_reference,
          }
        : undefined,
    transferDetailsAvailableUntil: paymentAPI.transfer_details_available_until,
    fundsReceivedAt: paymentAPI.funds_received_at,
    paidOutAt: paymentAPI.paid_out_at,
    quote: transformContractorPaymentQuoteFromAPI(paymentAPI.quote),
  };
};

export const transformContractorPaymentFeeFromApi = (feeAPI: FeeAPI): Fee => ({
  amount: feeAPI.amount,
  type: feeAPI.type,
  month: feeAPI.month,
});
